import React, { Suspense } from 'react'
import {
  ProductCarousel,
  ProductCarouselDescription,
  ProductCarouselList,
  ProductCarouselTitle,
} from '@/checkout-instore-legacy/components/ProductCarousel'
import { graphql } from 'babel-plugin-relay/macro'
import { useLazyLoadQuery } from 'react-relay-offline'
import { ProductCarouselHeader } from '@/checkout-instore-legacy/components/ProductCarousel/ProductCarouselHeader'

import { SkeletonItem } from '../Skeleton'
import type { ProductsShelfItem as PropsProductsShelfItem } from './types'
import { ProductsShelfItem } from './ProductsShelfItem'
import type { ProductsShelfQuery } from './__generated__/ProductsShelfQuery.graphql'
import { getSimulationAddresses } from '../../../../../../core/utils/simulation'
import { ErrorBoundary } from '../../../../../../core/components'
import { ProductsShelfErrorState } from './ProductsShelfErrorState'

const productsShelfQuery = graphql`
  query ProductsShelfQuery(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $where: ProductsWhere
    $addresses: [SimulationAddressInput!]!
  ) {
    products(
      first: $first
      after: $after
      last: $last
      before: $before
      where: $where
    ) {
      edges {
        node {
          _id
        }
      }
      ...ProductsShelfItem_ProductConnection
    }
  }
`

const getItemKey = (item: PropsProductsShelfItem) =>
  `${item.productId}-${item.skuId}`

type ProductsShelfContentProps = {
  title: string
  description?: string
  items: PropsProductsShelfItem[]
}

export const ProductsShelfContent = ({
  title,
  description,
  items,
}: ProductsShelfContentProps) => {
  const simulationAddresses = getSimulationAddresses()

  const { data, error } = useLazyLoadQuery<ProductsShelfQuery>(
    productsShelfQuery,
    {
      first: 25,
      where: {
        id: {
          in: items.map((item) => item.productId),
        },
      },
      addresses: simulationAddresses,
    },
    {
      fetchPolicy: 'store-and-network',
      skip: !simulationAddresses.length,
    }
  )

  if (error) {
    throw new Error()
  }

  if (!data) {
    return null
  }

  const filteredItems = items.filter(
    (item) =>
      !!data.products.edges.find(
        (productEdge) => productEdge.node._id === item.productId
      )
  )

  return (
    <ProductCarousel>
      <ProductCarouselHeader>
        <ProductCarouselTitle>{title}</ProductCarouselTitle>
        <ProductCarouselDescription>{description}</ProductCarouselDescription>
      </ProductCarouselHeader>
      <ProductCarouselList>
        {filteredItems.map((item) => (
          <ProductsShelfItem
            key={getItemKey(item)}
            item={item}
            productConnectionReference={data.products}
          />
        ))}
      </ProductCarouselList>
    </ProductCarousel>
  )
}

export type ProductsShelfProps = ProductsShelfContentProps

export const ProductsShelf = (props: ProductsShelfProps) => (
  <ErrorBoundary
    fallback={(error, errorId) => {
      return (
        <>
          <ProductCarouselHeader>
            <ProductCarouselTitle>{props.title}</ProductCarouselTitle>
            <ProductCarouselDescription>
              {props.description}
            </ProductCarouselDescription>
          </ProductCarouselHeader>
          <ProductsShelfErrorState error={error} errorId={errorId} />
        </>
      )
    }}
  >
    <Suspense
      fallback={
        <ProductCarousel>
          <ProductCarouselHeader>
            <ProductCarouselTitle>{props.title}</ProductCarouselTitle>
            <ProductCarouselDescription>
              {props.description}
            </ProductCarouselDescription>
          </ProductCarouselHeader>
          <ProductCarouselList>
            {props.items.map((item) => (
              <SkeletonItem key={getItemKey(item)} />
            ))}
          </ProductCarouselList>
        </ProductCarousel>
      }
    >
      <ProductsShelfContent {...props} />
    </Suspense>
  </ErrorBoundary>
)
